import { createUserError } from "../utils/errorHandler";
import i18n from "../i18n";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from "./tokenService";

const API_BASE_URL = process.env.REACT_APP_API_URL;
// REACT_APP_API_URL pointe sur /api/v1 ; quelques endpoints n'existent qu'en v2.
const API_V2_BASE_URL = (API_BASE_URL || "").replace(/\/v1\/?$/, "/v2");
const API_KEY = process.env.REACT_APP_API_KEY;

// Configuration des headers pour les requêtes API
const getHeaders = (includeAuth = false, accessToken = null) => {

  const currentLanguage = i18n.language || localStorage.getItem("lang") || "fr";

  const headers = {
    'accept': 'application/json',
    'X-API-KEY': API_KEY,
    'X-Client-Type': 'onecc',
    'Content-Type': 'application/json',
    'Accept-Language': currentLanguage,
  };

  if (includeAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
};

// Fonction pour extraire le message d'erreur
const extractErrorMessage = (data) => {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return null;

  // Priorité aux messages directs
  if (data.message) return data.message;
  if (data.error && typeof data.error === 'string') return data.error;
  if (data.detail) return data.detail;

  // Recherche dans les objets imbriqués (ex: paymentMethod.details ou paymentMethod.error)
  for (const key in data) {
    if (typeof data[key] === 'object') {
      const nestedMessage = extractErrorMessage(data[key]);
      if (nestedMessage) return nestedMessage;
    }
  }

  return null;
};

// Fonction pour gérer les erreurs de réponse API
const handleApiError = async (response) => {
  let errorData;
  try {
    errorData = await response.json();
  } catch (e) {
    const error = new Error(`Invalid server response. (code de l'erreur: ${response.status})`);
    error.status = response.status;
    throw error;
  }

  const apiErrorMessage = extractErrorMessage(errorData);

  const error = new Error(apiErrorMessage || `Erreur ${response.status}`);
  error.status = response.status;
  error.data = errorData;
  error.isApiError = true;
  throw error;
};

// Renouvellement de l'accessToken a partir du refreshToken.
//
// L'accessToken expire au bout d'une heure. Sans renouvellement, une recharge
// qui dure plus longtemps se retrouve bloquee : la conso se fige et le bouton
// "Arreter la charge" repond 401, le client perd le controle de sa borne
// (QCT-688).
//
// Le endpoint attend le refreshToken en Bearer, pas dans le corps.
//
// refreshPromise deduplique les renouvellements : le polling de la recharge
// tourne toutes les 5 s, plusieurs requetes prennent donc le 401 en meme temps
// et ne doivent declencher qu'un seul appel a /auth/refresh.
let refreshPromise = null;

const requestNewAccessToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: getHeaders(true, refreshToken),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      // Seul un 401 signifie que le refreshToken est lui aussi perime : la
      // session est finie, on purge. Tout le reste (403 d'une passerelle ou
      // d'un anti-abus, 5xx passager) laisse les jetons en place : les
      // effacer condamnerait une session encore valide, sans recours possible
      // dans la page.
      if (response.status === 401) {
        clearTokens();
      }
      return null;
    }

    const data = await response.json();
    const accessToken = data.accessToken || data.access_token;

    if (!accessToken) {
      return null;
    }

    // Le back fait tourner le refreshToken, mais on ne s'y fie pas : s'il n'en
    // renvoie pas, on conserve celui qui a servi plutot que de se retrouver
    // sans aucun moyen de renouveler au bout de l'heure suivante.
    setTokens({
      accessToken,
      refreshToken: data.refreshToken || data.refresh_token || refreshToken,
    });
    return accessToken;
  } catch (error) {
    console.error('Erreur lors du renouvellement du jeton:', error);
    return null;
  }
};

const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = requestNewAccessToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

// Appel authentifie, avec renouvellement du jeton et rejeu en cas de 401.
//
// Le jeton stocke fait foi : les composants le recoivent via location.state au
// moment de la navigation, cette copie devient obsolete des le premier
// renouvellement. Le parametre fallbackToken ne sert qu'aux cas ou le stockage
// n'a pas encore ete alimente.
const authorizedFetch = async (url, options = {}, fallbackToken = null) => {
  const buildInit = (token) => ({ ...options, headers: getHeaders(true, token) });

  const tokenUsed = getAccessToken() || fallbackToken;
  let response = await fetch(url, buildInit(tokenUsed));

  if (response.status === 401) {
    // Un autre appel a pu renouveler le jeton pendant que celui-ci etait en
    // vol : on rejoue alors avec le jeton frais au lieu de redemander un
    // renouvellement, qui gacherait un aller-retour et echouerait la requete
    // si /auth/refresh repondait mal a ce moment-la.
    const storedToken = getAccessToken();
    const renewedToken =
      storedToken && storedToken !== tokenUsed
        ? storedToken
        : await refreshAccessToken();

    if (renewedToken) {
      response = await fetch(url, buildInit(renewedToken));
    }
  }

  if (!response.ok) {
    await handleApiError(response);
  }

  return await response.json();
};

// Récupérer les informations de la borne
export const getChargingStationInfo = async (evcpReference) => {
  try {
    const response = await fetch(`${API_BASE_URL}/evcps/${evcpReference}?article=CHARGE_NOW`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération des informations de la borne:', error);
    throw error;
  }
};

// Vérifier l'éligibilité au One-Click Charge
export const checkOneClickChargeEligibility = async (evcpReference) => {
  try {
    const response = await fetch(`${API_BASE_URL}/evcps/${evcpReference}/one-click-charge-eligibility`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'éligibilité:', error);
    throw error;
  }
};

// Configurer le One-Click Charge avec Stripe
export const setupOneClickCharge = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/onecc/setup`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        email,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la configuration du Scan to Charge:', error);
    throw error;
  }
};

// Reprendre une session via le token du lien email (Canal A — re-scan/lien email)
export const redeemSessionToken = async (sessionToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/onecc/sessions/${sessionToken}/token`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la reprise de session via le lien email:', error);
    throw error;
  }
};

// Reprendre une session active via les 4 derniers chiffres de la CB (re-scan QR)
export const resumeSessionByCard = async (connectorId, last4digits) => {
  try {
    const response = await fetch(`${API_BASE_URL}/onecc/sessions/resume`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        connectorId,
        last4digits,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la reprise de session:', error);
    throw error;
  }
};

// Récupérer l'URL signée de téléchargement du ticket de recharge.
// Le back renvoie { url } : une URL GCS signée valable 1h, à ouvrir telle quelle
// (elle porte sa propre autorisation, aucun token à rajouter dessus).
export const getInvoiceDownloadUrl = async (transactionId, accessToken) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/onecc/transactions/${transactionId}/invoice-download-url`,
      { method: 'GET' },
      accessToken
    );
  } catch (error) {
    console.error('Erreur lors de la récupération du ticket de recharge:', error);
    throw error;
  }
};

// Pré-autorisation CB
export const createAuthorization = async (chargeNowType, connectorId, accessToken) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/stripe/authorizations`,
      {
        method: 'POST',
        body: JSON.stringify({
          chargeNowType,
          connectorId
        }),
      },
      accessToken
    );
  } catch (error) {
    console.error('Erreur lors de la pré-autorisation:', error);
    throw error;
  }
};

export const attachePaymentMethod = async (paymentMethodId, accessToken) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/stripe/payment-methods`,
      {
        method: 'POST',
        body: JSON.stringify({
          paymentMethodId,
        }),
      },
      accessToken
    );
  } catch (error) {
    console.error("Erreur lors de l'attachement de la methode de paiement:", error);
    throw error;
  }
};

// Démarrer la charge
export const startCharging = async (evcpReference, connectorReference, accessToken) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/evcps/${evcpReference}/connectors/${connectorReference}/start-charge`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
      accessToken
    );
  } catch (error) {
    console.error('Erreur lors du démarrage de la charge:', error);
    throw error;
  }
};

// Réponse de la borne à la demande de démarrage (bornes V2 seulement).
// Le statut vit 5 min dans Redis côté QoREST, sous la clé renvoyée en `requestId`
// par start-charge ; passé ce délai, ou pour une clé inconnue, l'API répond 404
// avec status "not_found". L'endpoint n'existe qu'en v2.
export const getRemoteStartStatus = async (requestId, accessToken) => {
  return await authorizedFetch(
    `${API_V2_BASE_URL}/charging/${encodeURIComponent(requestId)}/status`,
    { method: 'GET' },
    accessToken
  );
};

// Suivre la transaction en cours. Le groupe "minimal" ajoute l'e-mail du client,
// affiché sur les écrans de recharge, y compris pour une session reprise.
export const getOngoingTransaction = async (accessToken) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/users/me?groups=minimal,ongoing-use`,
      { method: 'GET' },
      accessToken
    );
  } catch (error) {
    console.error('Erreur lors du suivi de la transaction:', error);
    throw error;
  }
};

// Historique des recharges du client, la plus récente en premier. Le prix (TTC)
// vaut 0 tant que la recharge n'a pas été valorisée par Qobilling.
export const getChargingHistory = async (accessToken, limit = 5) => {
  return await authorizedFetch(
    `${API_V2_BASE_URL}/charging/history?limit=${limit}`,
    { method: 'GET' },
    accessToken
  );
};

// Envoyer la facture
export const sendInvoice = async (paymentIntentId, invoiceData, accessToken, transactionId) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/onecc/invoice-delivery/`,
      {
        method: 'POST',
        body: JSON.stringify({
          paymentIntentId,
          transactionId,
          ...invoiceData,
        }),
      },
      accessToken
    );
  } catch (error) {
    console.error('Erreur lors de l\'envoi de la facture:', error);
    throw error;
  }
};

// Arreter la charge
export const stopCharging = async (evcpReference, accessToken) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/evcps/${evcpReference}/stop-charge`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
      accessToken
    );
  } catch (error) {
    console.error('Erreur lors de l\'arrêt de la charge:', error);
    throw error;
  }
};

// unlock 
export const unlockConnector = async (evcpReference, connectorNumber, accessToken) => {
  try {
    return await authorizedFetch(
      `${API_BASE_URL}/evcps/${evcpReference}/connectors/${connectorNumber}/unlock`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
      accessToken
    );
  } catch (error) {
    console.error('Erreur lors du déverrouillage du connecteur:', error);
    throw error;
  }
};